import { useCallback, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, CheckCircle, AlertCircle, Download, ListPlus } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Papa from "papaparse";
import { normalizeLinkedInUrl as normalizeLinkedInUrlHelper } from "@/lib/linkedinHelpers";

interface ColumnMapping {
  linkedinUrl: string;
  firstName: string;
  lastName: string;
  company: string;
  title: string;
}

interface CSVUploaderProps {
  onUpload: (targets: any[], headers: string[]) => void;
  uploadedCount: number;
  // Optional controlled state props for persistence
  initialParsedData?: any;
  initialColumnMapping?: ColumnMapping;
  initialUploadedTargets?: any[];
  onStateChange?: (state: { parsedData: any; columnMapping: ColumnMapping; uploadedTargets: any[] }) => void;
  startInUploadMode?: boolean;
}

export const CSVUploader = ({
  onUpload,
  uploadedCount,
  initialParsedData,
  initialColumnMapping,
  initialUploadedTargets,
  onStateChange,
  startInUploadMode = false
}: CSVUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedTargets, setUploadedTargets] = useState<any[]>(initialUploadedTargets ?? []);
  const [parsedData, setParsedData] = useState<any>(initialParsedData ?? null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>(initialColumnMapping ?? {
    linkedinUrl: "",
    firstName: "",
    lastName: "",
    company: "",
    title: ""
  });

  // Notify parent of state changes for persistence
  useEffect(() => {
    if (onStateChange) {
      onStateChange({ parsedData, columnMapping, uploadedTargets });
    }
  }, [parsedData, columnMapping, uploadedTargets]);

  const validateLinkedInUrl = (url: string): boolean => {
    if (!url || typeof url !== 'string') return false;

    // Trim whitespace and remove invisible Unicode characters
    const normalizedUrl = url.trim().replace(/[\u200B-\u200D\uFEFF]/g, '').toLowerCase();


    // Regex patterns for LinkedIn URLs - more reliable than includes()
    // Matches: linkedin.com/in/, www.linkedin.com/in/, or country.linkedin.com/in/
    const profilePattern = /linkedin\.com\/in\//;

    // Accept Sales Navigator patterns
    const salesLeadPattern = /linkedin\.com\/sales\/lead\//;
    const salesPeoplePattern = /linkedin\.com\/sales\/people\//;

    const isValid = profilePattern.test(normalizedUrl) ||
      salesLeadPattern.test(normalizedUrl) ||
      salesPeoplePattern.test(normalizedUrl);

    return isValid;
  };

  // Use the shared helper from src/lib/linkedinHelpers.ts for consistent normalization
  // This ensures frontend and backend use the same URL normalization logic
  const parseLinkedInURL = (url: string) => {
    // Use the shared normalizeLinkedInUrl helper
    const { publicUrl, profileId, urlType } = normalizeLinkedInUrlHelper(url);

    // Store Sales Nav URL separately if applicable
    const salesNavUrl = urlType === 'sales_nav' ? url : null;

    return {
      link_type: urlType === 'sales_nav' ? 'sales_nav' : 'profile_slug',
      profile_url: publicUrl, // Normalized URL
      linkedin_url: publicUrl, // For display
      sales_nav_url: salesNavUrl, // Store Sales Nav URL separately if applicable
      provider_id: profileId // Pre-extracted if available from Sales Nav
    };
  };

  const autoDetectColumns = (headers: string[]) => {
    const mapping = {
      linkedinUrl: "",
      firstName: "",
      lastName: "",
      company: "",
      title: ""
    };

    headers.forEach(header => {
      const lower = header.toLowerCase().trim();

      // LinkedIn URL detection
      if (!mapping.linkedinUrl && (
        lower.includes('linkedin') ||
        lower.includes('url') ||
        lower === 'profile' ||
        lower === 'link' ||
        lower === 'profile url'
      )) {
        mapping.linkedinUrl = header;
      }

      // First Name detection
      if (!mapping.firstName && (
        lower === 'first name' ||
        lower === 'firstname' ||
        lower === 'first_name' ||
        lower === 'fname'
      )) {
        mapping.firstName = header;
      }

      // Last Name detection
      if (!mapping.lastName && (
        lower === 'last name' ||
        lower === 'lastname' ||
        lower === 'last_name' ||
        lower === 'lname'
      )) {
        mapping.lastName = header;
      }

      // Company detection
      if (!mapping.company && (
        lower === 'company' ||
        lower === 'organization' ||
        lower === 'employer' ||
        lower === 'company name'
      )) {
        mapping.company = header;
      }

      // Title detection
      if (!mapping.title && (
        lower === 'title' ||
        lower === 'job title' ||
        lower === 'position' ||
        lower === 'role'
      )) {
        mapping.title = header;
      }
    });

    return mapping;
  };

  const parseCSV = (text: string, mapping: typeof columnMapping) => {
    const result = Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      dynamicTyping: false,
    });

    if (result.errors.length > 0) {
      throw new Error(`CSV parsing error: ${result.errors[0].message}`);
    }

    const headers = result.meta.fields || [];
    const seenUrls = new Set<string>();
    const MAX_TARGETS = 5000;
    let skippedDuplicates = 0;
    let skippedInvalid = 0;

    const targets = result.data
      .map((row: any, index: number) => {
        const linkedinUrl = row[mapping.linkedinUrl];

        // Skip rows with invalid LinkedIn URLs
        if (!validateLinkedInUrl(linkedinUrl)) {
          console.warn(`[CSV Upload] Row ${index + 2}: Skipping invalid LinkedIn URL: ${linkedinUrl}`);
          skippedInvalid++;
          return null;
        }

        const urlData = parseLinkedInURL(linkedinUrl);
        const normalizedUrl = urlData.profile_url.toLowerCase();

        // Skip duplicate URLs instead of throwing error
        if (seenUrls.has(normalizedUrl)) {
          console.warn(`[CSV Upload] Row ${index + 2}: Skipping duplicate LinkedIn URL: ${linkedinUrl}`);
          skippedDuplicates++;
          return null;
        }
        seenUrls.add(normalizedUrl);


        const firstName = mapping.firstName && mapping.firstName !== "_none"
          ? row[mapping.firstName]?.trim()
          : null;
        const lastName = mapping.lastName && mapping.lastName !== "_none"
          ? row[mapping.lastName]?.trim()
          : null;
        const company = mapping.company && mapping.company !== "_none"
          ? row[mapping.company]?.trim()
          : null;
        const title = mapping.title && mapping.title !== "_none"
          ? row[mapping.title]?.trim()
          : null;

        const mappedColumns = new Set([
          mapping.linkedinUrl,
          mapping.firstName,
          mapping.lastName,
          mapping.company,
          mapping.title
        ]);

        const customData: any = {};
        Object.entries(row).forEach(([key, value]) => {
          if (!mappedColumns.has(key) && value) {
            customData[key] = value;
          }
        });

        return {
          linkedin_url: urlData.profile_url,
          profile_url: urlData.profile_url,
          sales_nav_url: urlData.sales_nav_url,
          link_type: urlData.link_type,
          provider_id: urlData.provider_id, // Pre-extracted from Sales Nav if available
          first_name: firstName,
          last_name: lastName,
          company: company,
          title: title,
          name: firstName && lastName ? `${firstName} ${lastName}` : (firstName || lastName || null),
          custom_data: Object.keys(customData).length > 0 ? customData : null,
        };
      })
      .filter((target): target is NonNullable<typeof target> => target !== null);

    if (targets.length > MAX_TARGETS) {
      throw new Error(`CSV contains ${targets.length} rows. Maximum allowed: ${MAX_TARGETS}`);
    }

    // Log summary of skipped rows
    if (skippedDuplicates > 0 || skippedInvalid > 0) {
      console.log(`[CSV Upload] Summary: ${targets.length} valid targets, ${skippedDuplicates} duplicates skipped, ${skippedInvalid} invalid URLs skipped`);
    }

    return { targets, headers, skippedDuplicates, skippedInvalid };
  };

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    // Validate file size (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size: 10MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    // Check if file is empty
    if (file.size === 0) {
      toast.error("CSV file is empty");
      return;
    }

    setIsProcessing(true);
    try {
      const text = await file.text();

      // Validate minimum content
      if (text.trim().length === 0) {
        throw new Error("CSV file contains no data");
      }
      // Initial parse to get headers
      const result = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        preview: 1, // Just get headers
      });

      if (result.errors.length > 0) {
        throw new Error(`CSV parsing error: ${result.errors[0].message}`);
      }

      const headers = result.meta.fields || [];
      if (headers.length === 0) {
        throw new Error("CSV file appears to be empty or has no headers");
      }

      const detected = autoDetectColumns(headers);
      setColumnMapping(detected);
      setParsedData({ text, headers, previewRow: result.data[0], fileName: file.name });
      toast.success(`CSV loaded. Map your columns to continue.`);
    } catch (error: any) {
      toast.error("CSV Error: " + error.message);
      setParsedData(null);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleColumnSelect = useCallback(() => {
    if (!parsedData || !columnMapping.linkedinUrl || columnMapping.linkedinUrl === "_none") {
      toast.error("Please select the LinkedIn URL column");
      return;
    }

    setIsProcessing(true);
    try {
      const { targets, headers, skippedDuplicates, skippedInvalid } = parseCSV(parsedData.text, columnMapping);
      setUploadedTargets(targets);
      onUpload(targets, headers);
      setParsedData(null);

      const mappedFields = [
        columnMapping.firstName && columnMapping.firstName !== "_none" && "First Name",
        columnMapping.lastName && columnMapping.lastName !== "_none" && "Last Name",
        columnMapping.company && columnMapping.company !== "_none" && "Company",
        columnMapping.title && columnMapping.title !== "_none" && "Title"
      ].filter(Boolean);

      // Show success message with skipped counts if any
      let successMessage = `Successfully uploaded ${targets.length} targets`;
      if (mappedFields.length > 0) {
        successMessage += ` with ${mappedFields.join(", ")}`;
      }
      // toast.success(successMessage); // Removed toast notification

      // Show warning toast if duplicates or invalid URLs were skipped
      if (skippedDuplicates > 0 || skippedInvalid > 0) {
        const skippedParts = [];
        if (skippedDuplicates > 0) skippedParts.push(`${skippedDuplicates} duplicate${skippedDuplicates > 1 ? 's' : ''}`);
        if (skippedInvalid > 0) skippedParts.push(`${skippedInvalid} invalid URL${skippedInvalid > 1 ? 's' : ''}`);
        toast.warning(`Skipped ${skippedParts.join(' and ')}`);
      }
    } catch (error: any) {
      toast.error("CSV Error: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  }, [parsedData, columnMapping, onUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const downloadTemplate = () => {
    const template = `LinkedIn URL,First Name,Last Name,Company,Title,Custom Field 1
https://www.linkedin.com/in/johnsmith,John,Smith,Acme Corp,CEO,Value1
https://linkedin.com/in/janedoe,Jane,Doe,Tech Inc,CTO,Value3`;

    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "linkedin_targets_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("Template downloaded!");
  };

  // Always show the upload view directly - no selection cards needed

  return (
    <div className="space-y-4">

      {/* Column Mapper */}
      {parsedData && (
        <Card className="p-6 border-2 border-primary/20 bg-muted/10">
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Verify Column Mapping
                </h4>
                {parsedData.fileName && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1.5 bg-muted px-2.5 py-1 rounded-md">
                    <FileText className="w-3.5 h-3.5" />
                    {parsedData.fileName}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Match your CSV columns to the required fields. We've auto-selected the best matches based on your headers.
              </p>
            </div>

            <div className="space-y-6">
              {/* LinkedIn URL - REQUIRED */}
              <div className="bg-background p-4 rounded-lg border shadow-sm ring-1 ring-primary/10">
                <label className="text-sm font-medium mb-2 block">
                  LinkedIn URL Column <span className="text-destructive">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-4 items-start">
                  <Select
                    value={columnMapping.linkedinUrl}
                    onValueChange={(val) => setColumnMapping({ ...columnMapping, linkedinUrl: val })}
                  >
                    <SelectTrigger className={!columnMapping.linkedinUrl ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {parsedData.headers.map((header: string) => (
                        <SelectItem key={header} value={header}>{header}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {columnMapping.linkedinUrl && parsedData.previewRow && (
                    <div className="text-sm bg-muted/50 p-2.5 rounded border">
                      <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold block mb-0.5">Sample Value</span>
                      <span className="font-mono text-xs break-all line-clamp-2">
                        {parsedData.previewRow[columnMapping.linkedinUrl] || <span className="italic text-muted-foreground">Empty</span>}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Optional Fields Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { label: "First Name", key: "firstName" },
                  { label: "Last Name", key: "lastName" },
                  { label: "Company", key: "company" },
                  { label: "Title", key: "title" }
                ].map((field) => (
                  <div key={field.key} className="bg-background p-3 rounded-lg border">
                    <label className="text-xs font-medium mb-1.5 block text-muted-foreground uppercase tracking-wide">
                      {field.label}
                    </label>
                    <Select
                      value={(columnMapping as any)[field.key]}
                      onValueChange={(val) => setColumnMapping({ ...columnMapping, [field.key]: val })}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Skip..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Skip this field</SelectItem>
                        {parsedData.headers.map((header: string) => (
                          <SelectItem key={header} value={header}>{header}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {(columnMapping as any)[field.key] !== "_none" && parsedData.previewRow && (
                      <div className="mt-2 text-xs text-muted-foreground truncate">
                        Sample: <span className="font-medium text-foreground">
                          {parsedData.previewRow[(columnMapping as any)[field.key]] || "-"}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <Button
                onClick={handleColumnSelect}
                disabled={!columnMapping.linkedinUrl || columnMapping.linkedinUrl === "_none"}
                className="w-full py-6 text-base"
              >
                Import Contacts
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card
        className={`p-8 border-2 border-dashed transition-colors ${isDragging ? "border-primary bg-primary/5" : "border-border"
          }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className="text-center">
          {uploadedCount > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <h3 className="font-semibold">{uploadedCount} Targets Uploaded</h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUploadedTargets([]);
                    setParsedData(null);
                    setColumnMapping({
                      linkedinUrl: "",
                      firstName: "",
                      lastName: "",
                      company: "",
                      title: ""
                    });
                    document.getElementById("csv-input")?.click();
                  }}
                >
                  Upload Different File
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
              <h3 className="font-semibold text-lg">Upload Target List</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Drag and drop your CSV file here, or click to browse
              </p>
              <Button
                onClick={() => document.getElementById("csv-input")?.click()}
                disabled={isProcessing || parsedData !== null}
              >
                {isProcessing ? "Processing..." : "Choose File"}
              </Button>
            </div>
          )}
          <input
            id="csv-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileInput}
          />
        </div>
      </Card>

    </div>
  );
};
